import { MiddlewareAPI } from "redux";
import RootState from "store/state";
import { batch } from 'react-redux';
import { addSoftphoneError, addError } from "store/global/global.actions";
import * as callActions from 'store/call/call.actions';
import * as chatActions from 'store/chat/chat.actions';
import * as userActions from 'store/user/user.actions';
import * as ccpUtils from '../ccp.utils';
import GlobalTypes from "store/global/global.types";
import Logger from "logger";
import { canUserUseFeature } from 'utils';
import { AppFeatures } from 'hooks/useHasFeature';

export type MiddlewareStore = MiddlewareAPI<any, RootState>

const postMessageToSw = (type: string, data: any = {}) => {
    navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
            type,
            data
        })
    })
}

const listenToAgent = (
    store: MiddlewareStore,
) => {
    let sharedAgent: connect.Agent;
    let disallowedStatesTimeout: NodeJS.Timeout;

    connect.agent(agent => {
        sharedAgent = agent;

        //Attach agent to window for devtools purposes
        (window as any).agent = sharedAgent;

        if ((window as any).CallstatsAmazonShim) {
            const { callstats, name } = store.getState().app;
            if (callstats) {
                //@ts-ignore
                window.callstats = window.CallstatsAmazonShim.initialize(
                    connect,
                    callstats.appID,
                    callstats.apiKey,
                    `${name} - ${agent.getName()}`,
                    null,
                    () => console.log('Call stats init')
                );
            }
        }

        store.dispatch(
            userActions.userLoaded({
                timeRange: 'midnight',
                agentID: store.getState().auth.agentID!,
                name: agent.getName(),
                status: agent.getState() as connect.AgentStateDefinition,
                states: agent.getAgentStates(),
                username: agent.getConfiguration().username,
                softphoneEnabled: agent.getConfiguration().softphoneEnabled,
                queues: agent.getConfiguration().routingProfile.queues.filter(q => !!q.name),
                routingProfile: {
                    ID: agent.getRoutingProfile().routingProfileId,
                    name: agent.getRoutingProfile().name,
                },
                defaultOutboundQueueID: agent.getRoutingProfile().defaultOutboundQueue.queueARN.split('/').slice(-1)[0],
                inStateSince: new Date(Date.now() - agent.getStateDuration()),
                hierarchyStructure: store.getState().auth.hierarchyStructure,
                softphoneAutoAccept: agent.getConfiguration().softphoneAutoAccept
            })
        );

        const auth = store.getState().auth;
        const token = auth.refreshToken;
        const { identityManagement } = store.getState().app;
        if (!identityManagement || identityManagement === 'connect') {
            if (!token) {
                store.dispatch(userActions.setNotAvailable());
                store.dispatch(userActions.requestToken());
                window.postMessage({ type: 'READ_TOKEN' }, '*');
            } else if (!auth.authenticated) {
                store.dispatch(userActions.refreshToken());
            }
        }

        agent.onError(agent => {
            console.log('Agent transferred into an error state');
            const state = agent.getState();
            console.log('Agent in state:', state);
            const { app } = store.getState();
            // Check for an agent state explicitly forbidden in the app config
            if ((app.appConfig.disallowedStates || []).indexOf(state.name) > -1) {
                ccpUtils.setUserToPreviousState(store);
            } else {
                store.dispatch(userActions.userError());
            }
        });

        agent.onSoftphoneError(error => {
            const agentFeatures = store.getState().auth.features ?? [];
            const appFeatures = store.getState().app.features ?? [];

            const hasFeature = canUserUseFeature(appFeatures, agentFeatures);
            const disableForceAgentOffline = hasFeature(AppFeatures.DISABLE_FORCE_AGENT_OFFLINE);

            if (!store.getState().global.softphoneError && !disableForceAgentOffline) {
                Logger.error('SOFTPHONE ERROR', error);
                store.dispatch(addSoftphoneError(error.errorType));
                store.dispatch(addError('Detected softphone error from connect, make sure the connect CCP can access the microphone'));
                const offlineState = agent.getAgentStates().find((s) => s.type === connect.AgentStateType.OFFLINE)!;
                store.dispatch(userActions.setState(offlineState));
                postMessageToSw('forcedOffline', {
                    reason: "a softphone error"
                })
            }
        });

        agent.onStateChange(() => {
            const missedChat = store.getState().chat.connections.find(c =>
                c.status === connect.ContactStateType.MISSED ||
                c.status === connect.ContactStateType.ERROR
            );

            const { appConfig } = store.getState().app;
            if (appConfig.disallowedStates) {
                clearTimeout(disallowedStatesTimeout);
                disallowedStatesTimeout = setTimeout(() => {
                    const user = store.getState().user;
                    if (!user) return;

                    const { softphoneError } = store.getState().global;

                    if (appConfig?.disallowedStates?.includes(user.status.name) && !softphoneError) {
                        ccpUtils.setUserToPreviousState(store)
                    }

                }, 20_000);
            }

            if (missedChat) {
                store.dispatch(
                    chatActions.removeChatConnection(missedChat.id)
                )
            }
        })

        agent.onRefresh(agent => {
            //TODO... if no agent, can we just assume it's a refresh error, and just do nothing.
            const {
                user,
                call,
                global: { softphoneError },
                app: { appConfig },
            } = store.getState();
            if (!user || !user.status) return;
            //Clear contact if agent goes into Available or goes out of AfterCallWork
            if (
                ccpUtils.hasAgentChangedState(agent, user, 'Available') ||
                ccpUtils.hasAgentChangedState(agent, user, undefined, 'AfterCallWork')
            ) {
                if (call) {
                    store.dispatch(callActions.callEnded());
                }
            }

            if (ccpUtils.hasAgentChangedState(agent, user, 'Available', 'AfterCallWork')) {
                //After call work has ended and we've changed to available.
                store.dispatch(userActions.afterCallWorkEnd());
                store.dispatch(userActions.setAvailable());
            }
            if (ccpUtils.hasAgentChangedState(agent, user, 'AfterCallWork')) {
                //After call work is just starting, go into after call work.
                if (call && call.incoming) {
                    return store.dispatch(userActions.setAvailable());
                } else {
                    store.dispatch(userActions.afterCallWork());
                    //Navigate to dialler on after call work
                    store.dispatch({
                        type: GlobalTypes.DO_REDIRECT,
                        payload: "/"
                    })
                }
            }
            if (ccpUtils.hasAgentChangedState(agent, user, 'FailedConnectCustomer')) {
                console.log('Call failed to connect, this could be a headset issue');
                //TODO - despatch something to prompt them to replug the headset or at least refresh the page.
                store.dispatch(userActions.callIssue());
            }

            const agentQueues = user.queues || [];
            //rebuild queue list
            const queues = agent.getConfiguration().routingProfile.queues.filter(q => !!q.name);
            //attach existing data
            const userQueues = queues.map(queue => {
                const currentQueue = agentQueues.find(q => q.queueId === queue.queueId);
                if (!currentQueue) return queue;
                return {
                    ...queue,
                    stats: currentQueue.stats,
                    agentStats: currentQueue.agentStats,
                };
            });

            let afterCallStatus = user.afterCallStatus;
            let beforeCallStatus = user.beforeCallStatus;
            const hasAgentChangedState = ccpUtils.hasAgentChangedState(agent, user);
            const status = agent.getAgentStates().find(s => s.name === user.status.name);
            const isShowNextStatus = user.showNextStatus;
            //Previous status needs to be a status an agent can change to 
            if (hasAgentChangedState && status) {
                beforeCallStatus = status;
            }
            if (hasAgentChangedState && status && !isShowNextStatus) {
                afterCallStatus = status;
            }

            store.dispatch(
                userActions.userLoaded({
                    timeRange: user.timeRange || 'midnight',
                    agentID: store.getState().auth.agentID!,
                    name: agent.getName(),
                    status: agent.getState() as connect.AgentStateDefinition,
                    states: agent.getAgentStates(),
                    username: agent.getConfiguration().username,
                    softphoneEnabled: agent.getConfiguration().softphoneEnabled,
                    routingProfile: {
                        ID: agent.getRoutingProfile().routingProfileId,
                        name: agent.getRoutingProfile().name,
                    },
                    queues: userQueues,
                    afterCallStatus,
                    beforeCallStatus,
                    inStateSince: new Date(Date.now() - agent.getStateDuration()),
                    hierarchyStructure: store.getState().auth.hierarchyStructure
                })
            );
        });

        agent.onWebSocketConnectionLost(agent => {
            const agentFeatures = store.getState().auth.features ?? [];
            const appFeatures = store.getState().app.features ?? [];

            const hasFeature = canUserUseFeature(appFeatures, agentFeatures);
            const disableForceAgentOffline = hasFeature(AppFeatures.DISABLE_FORCE_AGENT_OFFLINE);

            Logger.warning('WEBSOCKET_CONNECTION_LOST', store.getState());
            store.dispatch(
                addError('The websocket connection has been lost.  Please reload and set your status')
            );

            if (!disableForceAgentOffline) {
                const offlineState = agent.getAgentStates().find((s) => s.type === "offline")!;
                store.dispatch(userActions.setState(offlineState));
                postMessageToSw('forcedOffline', {
                    reason: "a connection error"
                })
            }
        })

        agent.onEnqueuedNextState(agent => {
            const nextStatus = agent.getNextState();
            const statuses = agent.getAgentStates();
            const nextStateWithARN = statuses.find(s => s.name === nextStatus.name);

            batch(() => {
                store.dispatch(userActions.setAfterCallStatus(nextStateWithARN!));
                store.dispatch(userActions.setShowNextStatus(true));
            });
        })
    });

    return () => sharedAgent;
};

export default listenToAgent;